import { useCallback, useMemo, useState } from 'react';

import styled from 'styled-components';
import { Helmet } from 'react-helmet-async';
import { Plus } from 'lucide-react';

import { PAGES } from 'constants/pages';
import { useGetOrdersPaginatedQuery, type OrdersFilterInput, TotadminPermission } from 'data/__generated__';

import { DataView } from 'components/DataView';
import {
    PageActions,
    PageActionsMenu,
    PageBreadcrumb,
    PageBreadcrumbSegment,
    PageHeader,
    type PageActionGroup,
} from 'components/PageHeader';

import { DEFAULT_ORDER_FILTERS, ORDER_FILTERS, ORDERS_PAGE_SIZE, toOrdersFilterInput } from './constants/orderFilters';
import { buildOrderViews } from './constants/orderViews';

export const Orders = () => {
    // Le kanban regroupe en colonnes : une page courte laisserait des colonnes vides alors que leurs
    // commandes existent. D'où une première page large, et un bouton pour la suite.
    const [filters, setFilters] = useState<OrdersFilterInput>(DEFAULT_ORDER_FILTERS);

    const handleFiltersChange = useCallback((filterState: Record<string, unknown>) => {
        const next = toOrdersFilterInput(filterState);
        // La `DataView` remonte son état à chaque rendu : sans cette comparaison, la query repartirait
        // en boucle sur une valeur pourtant identique.
        setFilters((current) => (JSON.stringify(current) === JSON.stringify(next) ? current : next));
    }, []);

    const {
        data: ordersData,
        loading: ordersLoading,
        error: ordersError,
        fetchMore,
    } = useGetOrdersPaginatedQuery({
        fetchPolicy: 'cache-and-network',
        notifyOnNetworkStatusChange: true,
        variables: { offset: 0, limit: ORDERS_PAGE_SIZE, filters },
    });

    const orders = ordersData?.ordersFilter.orders ?? [];
    const hasMore = ordersData?.ordersFilter.hasMore ?? false;

    const [isLoadingMore, setIsLoadingMore] = useState(false);
    const loadMore = useCallback(async () => {
        setIsLoadingMore(true);
        try {
            await fetchMore({ variables: { offset: orders.length, limit: ORDERS_PAGE_SIZE, filters } });
        } finally {
            setIsLoadingMore(false);
        }
    }, [fetchMore, orders.length, filters]);

    const actionGroups: PageActionGroup[] = [
        {
            items: [
                {
                    label: 'Créer une commande',
                    icon: <Plus />,
                    to: PAGES.orderCreate.url,
                    dataTest: 'create-order',
                    requiredPermissions: [TotadminPermission.OrdersManage],
                },
            ],
        },
    ];

    const views = useMemo(
        () => buildOrderViews({ hasMore, isLoadingMore, onLoadMore: loadMore }),
        [hasMore, isLoadingMore, loadMore],
    );

    if (ordersError) {
        throw new Error('Une erreur est survenue lors de la récupération des commandes');
    }

    return (
        <Container>
            <Helmet title={`${PAGES.orders.label} · TOTADMIN`} />
            <PageHeader>
                <PageBreadcrumb>
                    <PageBreadcrumbSegment label={PAGES.orders.label} isCurrent />
                </PageBreadcrumb>
                <PageActions>
                    <PageActionsMenu groups={actionGroups} />
                </PageActions>
            </PageHeader>
            <Content>
                <DataView
                    data={orders}
                    getRowId={({ _id }) => _id}
                    isLoading={ordersLoading && !ordersData}
                    filters={ORDER_FILTERS}
                    onFiltersChange={handleFiltersChange}
                    // Pas d'`accessor` : la recherche part au SERVEUR. Sur une liste paginée, chercher
                    // dans les lignes chargées ne regarderait que la page en cours.
                    search={{ placeholder: 'Rechercher par ID, numéro, organisation ou totem' }}
                    views={views}
                />
            </Content>
        </Container>
    );
};

const Container = styled.div`
    display: flex;
    flex-direction: column;
    overflow: hidden;
    flex: 1;
    height: 100%;
    background-color: ${({ theme }) => theme.backgroundColor};
`;

const Content = styled.div`
    position: relative;
    display: flex;
    flex-direction: column;
    flex: 1;
    overflow: hidden;
    border-radius: 4px;
    margin: 16px;
    background-color: ${({ theme }) => theme.cardBackgroundColor};
`;
